import { useCallback, useMemo } from "react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Building2,
  ChevronDown,
  Lightbulb,
  Loader2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import { cardsToInsightLines } from "@/utils/insightActionPlanDefaults";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets, stripMd } from "@/utils/parseInsightBullets";
import {
  bulletsToActionRegister,
  bulletsToEnrolmentRecommendations,
  bulletsToExecutiveCards,
  bulletsToPriorityAlert,
  bulletsToPriorityItems,
  bulletsToRootCauseRows,
} from "@/utils/mistralInsightMappers";
import {
  buildEnrolmentAccordionCopy,
  buildEnrolmentActionRegister,
  buildEnrolmentBlockRiskIntensity,
  buildEnrolmentExecutiveCards,
  buildEnrolmentHeroMetrics,
  buildEnrolmentInsightsMarkdown,
  buildEnrolmentPriorityAlert,
  buildEnrolmentPriorityItems,
  buildEnrolmentRecommendations,
  buildEnrolmentRootCauseSignals,
  formatInsightUpdatedLabel,
} from "@/utils/enrolmentDerived";
import "./EnrolmentInsightsTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CARD_ICONS = {
  "enrolment-base": BarChart3,
  "gender-parity": Users,
  "retention-risk": Activity,
  "school-size": Building2,
};

const splitProseToPoints = (text) => {
  const clean = String(text || "").trim();
  if (!clean) return [];
  const parts = clean
    .split(/(?<=[.!?])\s+(?=[A-Z(])|;\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length ? parts : [clean];
};

const bulletsToList = (section, fallback) => {
  const bullets = parseInsightBullets(section);
  if (bullets.length) return bullets;
  const plain = stripMd(section);
  if (plain) return splitProseToPoints(plain);
  return splitProseToPoints(fallback);
};

const InsightDetailList = ({ items = [] }) => (
  <ul className="enr-in-details-list">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

export default function EnrolmentInsightsTab({
  overview,
  blockData = [],
  sizeDistribution = [],
}) {
  const hasData = Boolean(overview?.total_schools);

  const buildLocalMarkdown = useCallback(
    () => buildEnrolmentInsightsMarkdown(overview, blockData, sizeDistribution),
    [overview, blockData, sizeDistribution]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        e: overview?.grand_total,
        s: overview?.total_schools,
        g: overview?.gender_parity_index,
      }),
    [overview?.grand_total, overview?.total_schools, overview?.gender_parity_index]
  );

  const {
    markdown: aiMarkdown,
    loading: insightsLoading,
    generatedAt,
    refresh: refreshInsights,
    isMistral,
  } = useMistralFirstInsights({
    kind: "enrolment",
    title: "Enrolment Analytics Insights",
    buildLocalMarkdown,
    context: { overview, blockData, sizeDistribution },
    enabled: hasData,
    scopeKey,
  });

  const buckets = useMemo(() => splitIntoInsightBuckets(aiMarkdown), [aiMarkdown]);

  const heroMetrics = useMemo(
    () => buildEnrolmentHeroMetrics(overview, blockData, sizeDistribution),
    [overview, blockData, sizeDistribution]
  );

  const localPriorityAlert = useMemo(
    () => buildEnrolmentPriorityAlert(overview, blockData),
    [overview, blockData]
  );

  const localExecutiveCards = useMemo(
    () => buildEnrolmentExecutiveCards(overview, blockData, sizeDistribution),
    [overview, blockData, sizeDistribution]
  );

  const localRootCauseSignals = useMemo(
    () => buildEnrolmentRootCauseSignals(overview, blockData, sizeDistribution),
    [overview, blockData, sizeDistribution]
  );

  const localRecommendations = useMemo(
    () => buildEnrolmentRecommendations(overview, blockData),
    [overview, blockData]
  );

  const blockRiskRows = useMemo(() => buildEnrolmentBlockRiskIntensity(blockData), [blockData]);

  const localPriorityItems = useMemo(() => buildEnrolmentPriorityItems(overview), [overview]);

  const localActionRegister = useMemo(
    () => buildEnrolmentActionRegister(overview, blockData, sizeDistribution),
    [overview, blockData, sizeDistribution]
  );

  const priorityAlert = useMemo(() => {
    if (insightsLoading) return { ...localPriorityAlert, show: false };
    const rootBullets = parseInsightBullets(buckets.root);
    return rootBullets.length
      ? bulletsToPriorityAlert(rootBullets, localPriorityAlert)
      : localPriorityAlert;
  }, [insightsLoading, buckets.root, localPriorityAlert]);

  const executiveCards = useMemo(() => {
    if (insightsLoading) return [];
    const aiCards = bulletsToExecutiveCards(
      parseInsightBullets(buckets.insights),
      localExecutiveCards
    );
    return aiCards?.length ? aiCards : localExecutiveCards;
  }, [insightsLoading, buckets.insights, localExecutiveCards]);

  const rootCauseSignals = useMemo(() => {
    if (insightsLoading) return [];
    const aiRows = bulletsToRootCauseRows(
      parseInsightBullets(buckets.root),
      localRootCauseSignals
    );
    return aiRows?.length ? aiRows : localRootCauseSignals;
  }, [insightsLoading, buckets.root, localRootCauseSignals]);

  const recommendations = useMemo(() => {
    if (insightsLoading) return [];
    const aiRecs = bulletsToEnrolmentRecommendations(
      parseInsightBullets(buckets.recs),
      localRecommendations
    );
    return aiRecs?.length ? aiRecs : localRecommendations;
  }, [insightsLoading, buckets.recs, localRecommendations]);

  const priorityItems = useMemo(() => {
    if (insightsLoading) return [];
    const aiItems = bulletsToPriorityItems(
      parseInsightBullets(buckets.actions),
      localPriorityItems
    );
    return aiItems?.length ? aiItems : localPriorityItems;
  }, [insightsLoading, buckets.actions, localPriorityItems]);

  const actionRegister = useMemo(() => {
    if (insightsLoading) return [];
    const aiRegister = bulletsToActionRegister(
      parseInsightBullets(buckets.actions),
      localActionRegister
    );
    return aiRegister?.length ? aiRegister : localActionRegister;
  }, [insightsLoading, buckets.actions, localActionRegister]);

  const defaultRoot = useMemo(
    () => cardsToInsightLines(localRootCauseSignals),
    [localRootCauseSignals]
  );
  const defaultRecs = useMemo(
    () => cardsToInsightLines(localRecommendations),
    [localRecommendations]
  );
  const defaultActions = useMemo(
    () => cardsToInsightLines(localPriorityItems),
    [localPriorityItems]
  );

  const updatedLabel = useMemo(
    () =>
      insightsLoading
        ? "Generating insights…"
        : generatedAt
          ? formatInsightUpdatedLabel(generatedAt)
          : "Ready to generate",
    [insightsLoading, generatedAt]
  );

  const handleGenerateInsights = useCallback(async () => {
    const usedProvider = await refreshInsights();
    toast.success(
      usedProvider === "mistral"
        ? "Insights refreshed with Mistral AI."
        : "Insights refreshed (rule-based fallback)."
    );
  }, [refreshInsights]);

  if (!hasData) {
    return (
      <div className="enr-in-empty">
        <Lightbulb size={40} style={{ margin: "0 auto 12px", opacity: 0.4 }} />
        <p>No insights data available for the selected scope.</p>
      </div>
    );
  }

  return (
    <div className="enr-in-tab">
      <section className="enr-in-hero-card" id="enr-in-hero">
        <div className="enr-in-insight-hero">
          <div className="enr-in-hero-copy-block">
            <div className="enr-in-hero-icon" aria-hidden="true">
              <Lightbulb size={30} strokeWidth={1.8} />
            </div>
            <div>
              <h2>AI-Powered Enrolment Insights for Faster District Action</h2>
              <p>
                Generate explainable insights, root-cause signals, and priority actions from
                enrolment, gender parity, retention, and school-size indicators.
              </p>
              <div className="enr-in-hero-action-row">
                <button
                  type="button"
                  className="enr-in-primary-action"
                  onClick={handleGenerateInsights}
                  disabled={insightsLoading}
                >
                  {insightsLoading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Lightbulb size={18} />
                  )}
                  Generate Insights
                </button>
                <span className="enr-in-updated-pill">{updatedLabel}</span>
                {isMistral && generatedAt ? (
                  <span className="enr-in-updated-pill enr-in-mistral-pill">Mistral AI</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="enr-in-mini-metric-grid" aria-label="Insights summary metrics">
            <article className="enr-in-mini-card">
              <small>Total Enrolment</small>
              <strong>{heroMetrics.totalEnrolment}</strong>
              <span>{heroMetrics.schoolsLabel}</span>
            </article>
            <article className="enr-in-mini-card">
              <small>Girls Participation</small>
              <strong>{heroMetrics.girlsPct}</strong>
              <span>{heroMetrics.gpiLabel}</span>
            </article>
            <article className="enr-in-mini-card warning">
              <small>Retention Watch</small>
              <strong>{heroMetrics.retentionWatchCount} Blocks</strong>
              <span>{heroMetrics.retentionWatchBlocks}</span>
            </article>
            <article className="enr-in-mini-card danger">
              <small>Priority Actions</small>
              <strong>{heroMetrics.priorityActions}</strong>
              <span>{heroMetrics.priorityActionsLabel}</span>
            </article>
          </div>
        </div>
      </section>

      {priorityAlert.show ? (
        <section className="enr-in-alert-card">
          <div className="enr-in-icon-box red">
            <AlertTriangle size={22} />
          </div>
          <div>
            <h3>{toTitleCase(priorityAlert.title)}</h3>
            <p>{priorityAlert.body}</p>
          </div>
          <button
            type="button"
            className="enr-in-section-action"
            onClick={() => scrollToId("enr-in-actions")}
          >
            View action queue →
          </button>
        </section>
      ) : null}

      <div className="enr-in-section-title">
        <div>
          <h2>Executive Insight Cards</h2>
          <p>
            Top-level messages are grouped as decisions, not raw text. Each card connects a metric,
            an interpretation, and a recommended attention area.
          </p>
        </div>
        <button
          type="button"
          className="enr-in-section-action"
          onClick={() => scrollToId("enr-in-details")}
        >
          Detailed evidence ↓
        </button>
      </div>

      <section className="enr-in-insight-grid">
        {executiveCards.map((card) => {
          const Icon = CARD_ICONS[card.id] || BarChart3;
          return (
            <article key={card.id} className="enr-in-insight-card">
              <div className="enr-in-insight-head">
                <div className={`enr-in-icon-box ${card.iconClass}`}>
                  <Icon size={22} />
                </div>
                <span className={`enr-in-badge ${card.badgeClass}`}>{card.badge}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
              <div className="enr-in-insight-metric">
                <strong>{card.metric}</strong>
                <span>{card.metricUnit}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="enr-in-section-title">
        <div>
          <h2>Root-Cause Signals and Recommendations</h2>
          <p>
            Signals are shown as diagnostic evidence, while recommendations are shown as owner-ready
            actions.
          </p>
        </div>
        <button
          type="button"
          className="enr-in-section-action"
          onClick={() => scrollToId("enr-in-actions")}
        >
          Go to action register →
        </button>
      </div>

      <section className="enr-in-analytics-grid">
        <article className="enr-in-panel">
          <div className="enr-in-panel-head">
            <div>
              <h3>Root-Cause Signal Strength</h3>
              <p>
                Estimated signal intensity from enrolment, retention, parity, and school-size
                dimensions.
              </p>
            </div>
            <span className="enr-in-pill">Diagnostic</span>
          </div>
          <div className="enr-in-root-cause-map">
            {rootCauseSignals.map((row) => (
              <div key={row.label} className="enr-in-cause-row">
                <div className="enr-in-cause-label">{row.label}</div>
                <div className="enr-in-track">
                  <i className={row.tone} style={{ width: `${row.width}%` }} />
                </div>
                <div className="enr-in-cause-value">{row.value}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="enr-in-panel">
          <div className="enr-in-panel-head">
            <div>
              <h3>Recommended Interventions</h3>
              <p>Prioritized for district review and block-level ownership.</p>
            </div>
            <span className="enr-in-pill">Owner-ready</span>
          </div>
          <div className="enr-in-recommendation-list">
            {recommendations.map((rec) => (
              <div key={rec.num} className="enr-in-recommendation-item">
                <span className={`enr-in-num ${rec.tone}`}>{rec.num}</span>
                <div>
                  <h4>{rec.title}</h4>
                  <p>{rec.body}</p>
                </div>
                <span className={`enr-in-badge ${rec.badgeClass}`}>{rec.badge}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="enr-in-section-title">
        <div>
          <h2>Priority Action Queue</h2>
          <p>
            Actions are organized by what needs immediate executive attention, what requires
            block-level diagnosis, and what can be monitored.
          </p>
        </div>
        <button type="button" className="enr-in-section-action" onClick={() => scrollToId("enr-in-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="enr-in-action-grid" id="enr-in-actions">
        <article className="enr-in-panel">
          <div className="enr-in-panel-head">
            <div>
              <h3>Block Risk Intensity</h3>
              <p>Priority blocks based on retention and parity signals.</p>
            </div>
            <span className="enr-in-pill">Top focus</span>
          </div>
          <div className="enr-in-stage-card">
            {blockRiskRows.length ? (
              blockRiskRows.map((row) => (
                <div key={row.name} className="enr-in-stage-row">
                  <b>{row.name}</b>
                  <div className="enr-in-stage-line">
                    <i className={row.tone} style={{ width: `${row.width}%` }} />
                  </div>
                  <span>{row.label}</span>
                </div>
              ))
            ) : (
              <p className="enr-in-muted-copy">No block-level retention data for the current scope.</p>
            )}
          </div>
        </article>

        <article className="enr-in-panel">
          <div className="enr-in-panel-head">
            <div>
              <h3>Priority Items</h3>
              <p>Decision list for the next district review meeting.</p>
            </div>
            <span className="enr-in-pill">Action queue</span>
          </div>
          <div className="enr-in-priority-list">
            {priorityItems.map((item) => (
              <div key={item.rank} className="enr-in-priority-item">
                <span className={`enr-in-num ${item.tone}`}>{item.rank}</span>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
                <span className={`enr-in-badge ${item.badgeClass}`}>{item.badge}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="enr-in-action-plan" id="enr-in-details">
        <InterventionActionPlan
          aiContent={aiMarkdown}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <section className="enr-in-table-card">
        <div className="enr-in-table-head">
          <div>
            <h3>Priority Action Register</h3>
            <p>Ready-to-use action log for district and block-level follow-up.</p>
          </div>
          <span className="enr-in-pill">Next review</span>
        </div>
        <div className="enr-in-table-scroll">
          <table aria-label="Priority action register">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Theme</th>
                <th>Signal</th>
                <th>Recommended Action</th>
                <th>Owner</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {actionRegister.map((row, index) => (
                <tr key={`${row.priority}-${row.theme}-${index}`}>
                  <td>{row.priority}</td>
                  <td>{row.theme}</td>
                  <td>{row.signal}</td>
                  <td>{row.action}</td>
                  <td>{row.owner}</td>
                  <td>
                    <span className={`enr-in-status ${row.statusClass}`}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
